/**
 * Date/Time Utility Functions
 *
 * Purpose: Centralized date and time operations for the OrchestrateHQ AI frontend
 *          including formatting, parsing, and timezone handling.
 *
 * Architecture: Part of the frontend utilities module, used across all components
 *              for consistent date/time handling and formatting.
 *
 * Dependencies: date-fns (arithmetic), date-fns-tz, Intl (display), utils/locale
 * Author: Development Team
 * Created: 2025-01-27
 */

// Optimized imports - only import what we actually use
import { parseISO, isValid, differenceInDays, addDays, startOfDay, endOfDay } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { getActiveHourCycle, getActiveLocale, HourCycle } from './locale';

/**
 * Display presets (ORCH-191). A date-fns pattern is inherently monolingual — no
 * pattern is right in both en-US and de-DE — so display goes through
 * `Intl.DateTimeFormat`'s `dateStyle` / `timeStyle` and the active locale decides
 * order, separators and month names. date-fns stays for arithmetic and the picker.
 *
 * | preset   | en-US                 | de-DE              |
 * |----------|-----------------------|--------------------|
 * | `short`  | 5/22/26               | 22.05.26           |
 * | `medium` | May 22, 2026          | 22.05.2026         |
 * | `long`   | May 22, 2026          | 22. Mai 2026       |
 * | `full`   | Friday, May 22, 2026  | Freitag, 22. Mai 2026 |
 */
export type DatePreset = 'short' | 'medium' | 'long' | 'full';
/** `short` → 2:30 PM / 14:30; `medium` adds seconds. */
export type TimePreset = 'short' | 'medium';

const toDate = (date: Date | string): Date | null => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return isValid(dateObj) ? dateObj : null;
};

/**
 * Build the Intl options for a date and/or time preset
 *
 * Logic: `hourCycle` is only passed when the user chose a clock; otherwise the
 *        locale decides 12- vs 24-hour. `hourCycle` rather than `hour12` because
 *        `hour12: false` maps to `h24` in some locales (ja-JP shows midnight as 24:00).
 */
const intlOptions = (
  dateStyle?: DatePreset,
  timeStyle?: TimePreset,
  hourCycle: HourCycle = getActiveHourCycle()
): Intl.DateTimeFormatOptions => ({
  ...(dateStyle && { dateStyle }),
  ...(timeStyle && { timeStyle }),
  ...(timeStyle && hourCycle && { hourCycle }),
});

const formatWithIntl = (
  dateObj: Date,
  options: Intl.DateTimeFormatOptions,
  locale: string
): string => {
  try {
    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(dateObj);
  }
};

/**
 * Format a date for display in the user's locale
 *
 * @param date - Date to format (Date or ISO string)
 * @param preset - `short` | `medium` (default) | `long`
 * @param locale - BCP 47 tag (default: the active locale)
 * @returns Formatted date string, or 'Invalid Date'
 */
export const formatDate = (
  date: Date | string,
  preset: DatePreset = 'medium',
  locale: string = getActiveLocale()
): string => {
  const dateObj = toDate(date);
  if (!dateObj) {
    return 'Invalid Date';
  }
  return formatWithIntl(dateObj, intlOptions(preset), locale);
};

/**
 * Format a date and time for display in the user's locale and clock
 *
 * @param date - Date to format (Date or ISO string)
 * @param datePreset - `short` | `medium` (default) | `long`
 * @param timePreset - `short` (default) | `medium`
 * @param locale - BCP 47 tag (default: the active locale)
 * @param hourCycle - `h12` / `h23` to force a clock (default: the user's Clock, else the locale's)
 * @returns Formatted datetime string ("May 22, 2026, 2:30 PM" / "22.05.2026, 14:30"), or 'Invalid Date'
 */
export const formatDateTime = (
  date: Date | string,
  datePreset: DatePreset = 'medium',
  timePreset: TimePreset = 'short',
  locale: string = getActiveLocale(),
  hourCycle: HourCycle = getActiveHourCycle()
): string => {
  const dateObj = toDate(date);
  if (!dateObj) {
    return 'Invalid Date';
  }
  return formatWithIntl(dateObj, intlOptions(datePreset, timePreset, hourCycle), locale);
};

/**
 * Format a time of day for display in the user's locale and clock
 *
 * @param date - Date to format (Date or ISO string)
 * @param preset - `short` (default) | `medium`
 * @param locale - BCP 47 tag (default: the active locale)
 * @param hourCycle - `h12` / `h23` to force a clock (default: the user's Clock, else the locale's)
 * @returns Formatted time string ("2:30 PM" / "14:30"), or 'Invalid Date'
 */
export const formatTime = (
  date: Date | string,
  preset: TimePreset = 'short',
  locale: string = getActiveLocale(),
  hourCycle: HourCycle = getActiveHourCycle()
): string => {
  const dateObj = toDate(date);
  if (!dateObj) {
    return 'Invalid Date';
  }
  return formatWithIntl(dateObj, intlOptions(undefined, preset, hourCycle), locale);
};

/**
 * A locale's numeric short-date pattern and a sample in it (ORCH-413)
 *
 * Logic: Built from `formatToParts` on a 2-digit day/month, 4-digit year format:
 *        year/month/day parts become YYYY/MM/DD, literals pass through.
 *
 * @param date - The sample date
 * @param locale - BCP 47 tag (default: the active locale)
 * @returns `{ pattern, sample }` — e.g. `{ pattern: 'DD.MM.YYYY', sample: '22.05.2026' }`
 */
export const shortDatePattern = (
  date: Date,
  locale: string = getActiveLocale()
): { pattern: string; sample: string } => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat(locale, options);
  } catch {
    formatter = new Intl.DateTimeFormat(undefined, options);
  }
  const tokens: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {
    year: 'YYYY',
    month: 'MM',
    day: 'DD',
  };
  const parts = formatter.formatToParts(date);
  return {
    pattern: parts.map((part) => tokens[part.type] ?? part.value).join(''),
    sample: parts.map((part) => part.value).join(''),
  };
};

/**
 * The clock a locale and Clock setting resolve to: true when times show AM/PM
 *
 * Purpose: MUI time pickers take an `ampm` prop; this keeps typed input on the same
 *          clock as the displayed one.
 */
export const usesTwelveHourClock = (
  locale: string = getActiveLocale(),
  hourCycle: HourCycle = getActiveHourCycle()
): boolean => {
  try {
    // `hourCycle` is in the ECMA-402 resolved options but not yet in the TS lib typings
    const resolved = (
      new Intl.DateTimeFormat(
        locale,
        intlOptions(undefined, 'short', hourCycle)
      ).resolvedOptions() as {
        hourCycle?: string;
      }
    ).hourCycle;
    return resolved === 'h12' || resolved === 'h11';
  } catch {
    return hourCycle !== 'h23';
  }
};

/**
 * Format date relative to current time (e.g., "2 days ago")
 *
 * Purpose: Provides human-readable relative time for better UX
 * Logic: Calculates difference from current date and formats accordingly
 *
 * @param date - Date to format
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    return 'Invalid Date';
  }

  const now = new Date();
  const diffInDays = differenceInDays(now, dateObj);

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays > 1 && diffInDays <= 7) {
    return `${diffInDays} days ago`;
  } else {
    return formatDate(dateObj);
  }
};

/**
 * Convert date to specific timezone
 *
 * Purpose: Handles timezone conversion for user's local time
 * Logic: Uses date-fns-tz to convert UTC dates to user's timezone
 *
 * @param date - UTC date to convert
 * @param timezone - Target timezone (e.g., 'America/New_York')
 * @returns Date in specified timezone
 */
export const convertToTimezone = (date: Date | string, timezone: string): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    throw new Error('Invalid date provided');
  }

  return toZonedTime(dateObj, timezone);
};

/**
 * Format date in specific timezone
 *
 * Purpose: Displays dates in user's local timezone
 * Logic: Converts UTC date to timezone and formats for display
 *
 * @param date - UTC date to format
 * @param timezone - Target timezone
 * @param formatString - Format pattern
 * @returns Formatted date string in timezone
 */
export const formatDateInTimezone = (
  date: Date | string,
  timezone: string,
  formatString: string = 'MMM dd, yyyy HH:mm'
): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    return 'Invalid Date';
  }

  return formatInTimeZone(dateObj, timezone, formatString);
};

/**
 * Check if date is today
 *
 * Purpose: Determines if a date falls on the current day
 * Logic: Compares date with start/end of current day
 *
 * @param date - Date to check
 * @returns True if date is today
 */
export const isToday = (date: Date | string): boolean => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    return false;
  }

  const today = new Date();
  const startOfToday = startOfDay(today);
  const endOfToday = endOfDay(today);

  return dateObj >= startOfToday && dateObj <= endOfToday;
};

/**
 * Add business days to date (skipping weekends)
 *
 * Purpose: Calculates future business dates for planning
 * Logic: Iteratively adds days, skipping Saturday and Sunday
 *
 * @param date - Starting date
 * @param days - Number of business days to add
 * @returns Resulting business date
 */
export const addBusinessDays = (date: Date | string, days: number): Date => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    throw new Error('Invalid date provided');
  }

  let result = dateObj;
  let remainingDays = Math.abs(days);
  const direction = days > 0 ? 1 : -1;

  while (remainingDays > 0) {
    result = addDays(result, direction);
    const dayOfWeek = result.getDay();

    // Skip weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      remainingDays--;
    }
  }

  return result;
};
